import React from 'react';
import {
  Image,
  ImageStyle,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

/** Official BookAPT app icon (512×512) — synced with public/bookapt-logo.png for web. */
export const BOOKAPT_LOGO_SOURCE = require('../assets/images/bookapt-logo.png');

export type BookAPTLogoSize = 'sm' | 'md' | 'lg' | 'header';

const SIZE_MAP: Record<BookAPTLogoSize, { card: number; image: number; radius: number }> = {
  sm: { card: 48, image: 40, radius: 12 },
  md: { card: 80, image: 68, radius: 18 },
  lg: { card: 96, image: 80, radius: 20 },
  header: { card: 108, image: 92, radius: 22 },
};

function resolveLogoDims(size: BookAPTLogoSize | number): { card: number; image: number; radius: number } {
  if (typeof size === 'number') {
    const card = size;
    return {
      card,
      image: Math.round(card * 0.85),
      radius: Math.round(card * 0.22),
    };
  }
  return SIZE_MAP[size] ?? SIZE_MAP.md;
}

interface BookAPTLogoProps {
  size?: BookAPTLogoSize | number;
  /** White rounded backing — use on dark headers */
  showCardBackground?: boolean;
  onPress?: () => void;
  style?: ViewStyle;
  imageStyle?: ImageStyle;
}

const BookAPTLogo: React.FC<BookAPTLogoProps> = ({
  size = 'md',
  showCardBackground = false,
  onPress,
  style,
  imageStyle,
}) => {
  const dims = resolveLogoDims(size);
  const logoSource =
    Platform.OS === 'web'
      ? { uri: '/bookapt-logo.png' }
      : BOOKAPT_LOGO_SOURCE;

  const content = (
    <View
      style={[
        showCardBackground ? styles.card : styles.plain,
        showCardBackground && {
          width: dims.card,
          height: dims.card,
          borderRadius: dims.radius,
        },
        style,
      ]}
    >
      <Image
        source={logoSource}
        style={[
          {
            width: showCardBackground ? dims.image : dims.card,
            height: showCardBackground ? dims.image : dims.card,
            borderRadius: showCardBackground ? 0 : dims.radius,
          },
          imageStyle,
        ]}
        resizeMode="cover"
        accessibilityLabel="BookAPT logo"
      />
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} activeOpacity={0.85} accessibilityRole="button">
        {content}
      </TouchableOpacity>
    );
  }

  return content;
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  plain: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default BookAPTLogo;
